<script lang="ts">
/**
 * Anchored overlays that are not clipped by whatever they grew out of.
 *
 * Every overlay in the editor was born inside a scroll or clip container, and
 * each one broke in its own way: the palette's description strip resized the
 * list above it as the pointer moved (43px of movement measured, with the
 * hovered row pushed off the top of the viewport when the list was already
 * scrolled to its end), the variable picker's result list pushed the inspector
 * form around and lost 63px of itself to the pane's edge, `UiFilterSelect`'s
 * `absolute` popup was cut by the 52px top bar, and a socket's popover was cut
 * by the canvas pane AND moved by Vue Flow's viewport transform. One cause,
 * four costumes: an overlay positioned against an ancestor inherits that
 * ancestor's `overflow`.
 *
 * So the overlay leaves the tree. `<Teleport>` to `body` plus `position:
 * fixed` escapes every `overflow` ancestor at once, and the price of leaving
 * the tree — the panel no longer moves with its anchor — is what the rest of
 * this file pays: it measures the anchor's client rect, flips to the other
 * side and shifts along the cross axis to stay inside the viewport, and
 * re-measures when anything moves.
 *
 * A component rather than a composable: what four call sites would otherwise
 * duplicate is not the arithmetic, it is the `<Teleport>` plus the positioned
 * wrapper plus the listener lifecycle. A composable would hand back a style
 * object and leave that markup to be re-typed four times.
 *
 * Positioning is written straight onto the element instead of through a
 * reactive style object. The geometry is not state anyone else reads, it is
 * recomputed from measurements on every scroll frame, and routing it through
 * reactivity would put a render between measuring and placing.
 *
 * Deliberately NOT here: chrome. A caller passes its own border, background
 * and shadow through `class`, because the four panels do not look alike and a
 * `variant` prop enumerating them would be this component knowing about them.
 */
import { onBeforeUnmount, onUpdated, ref, watch } from 'vue'

/** The side of the anchor the panel prefers; it may end up on the other one. */
export type FloatingSide = 'bottom' | 'top' | 'right' | 'left'
/** Which edge of the panel lines up with the anchor's, on the cross axis. */
export type FloatingAlign = 'start' | 'end'

interface Layer {
  panel: () => HTMLElement | null
  anchor: () => HTMLElement | null
  dismissible: boolean
}

/**
 * Open panels, oldest first. Two things need it, and both are about NESTING:
 * a socket's popover holds the variable picker, whose own panel is a sibling
 * in `body` rather than a descendant, so `contains()` on either one alone
 * would read a click on a search result as "outside" and close the popover
 * before the click could land. Escape has the mirror problem and has to reach
 * the innermost panel only.
 */
const layers: Layer[] = []

/** Above the editor's own `z-50` chrome, with room to stack nested panels. */
const BASE_Z = 1000

/** Keeps a panel off the very edge of the window. */
const VIEWPORT_MARGIN = 8

/**
 * A panel squeezed below this is not worth capping to; it is shifted instead,
 * overlapping its anchor, which is still better than being unreadable.
 */
const MIN_PANEL = 120

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), Math.max(low, high))
}

/**
 * Whether to stay on the preferred side. The panel gives it up only when it
 * does not fit there AND the other side is roomier, so a panel that fits does
 * not jump about, and one that fits nowhere lands where there is most space.
 */
function keepSide(needed: number, preferred: number, other: number): boolean {
  return needed <= preferred || preferred >= other
}
</script>

<script setup lang="ts">
/** `class` and `aria-*` belong to the teleported panel, not to a wrapper. */
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    /**
     * What the panel is measured from, and what counts as "inside" for
     * outside-dismissal: a pointerdown on the trigger has to reach the
     * trigger's own toggle instead of being dismissed out from under it.
     */
    anchor: HTMLElement | null
    open: boolean
    side?: FloatingSide
    align?: FloatingAlign
    /** Space between the anchor's edge and the panel's, in px. */
    gap?: number
    /** Dropdowns want the trigger's width; a `min-w-*` class still wins. */
    matchAnchorWidth?: boolean
    /**
     * False for a tooltip: it takes no pointer events and has no dismissal of
     * its own, because whatever drives the hover already owns its lifetime.
     */
    interactive?: boolean
    /** Whether a scroll or resize under an open panel re-places it or closes it. */
    follow?: boolean
  }>(),
  {
    side: 'bottom',
    align: 'start',
    gap: 4,
    matchAnchorWidth: false,
    interactive: true,
    follow: true,
  },
)

const emit = defineEmits<{ close: [] }>()

const panelEl = ref<HTMLElement | null>(null)

/**
 * Whether the panel has been measured and placed. It hides through a CLASS
 * rather than through the `style` attribute, and `top`/`left` are never in
 * the template either, because of a real failure this cost an afternoon:
 * `v-bind="$attrs"` rebuilds the props object on every render, so Vue
 * re-applies whatever `style` the template declares — silently resetting
 * `top` and `left` to the placeholder the moment the slot's content changed.
 * Anything this file writes imperatively has to be absent from the template.
 */
const placed = ref(false)

/** The size we last imposed, so the observer can tell our writes from content. */
let imposed = { width: -1, height: -1 }
let observer: ResizeObserver | undefined
let zIndex = BASE_Z

const layer: Layer = {
  panel: () => panelEl.value,
  anchor: () => props.anchor,
  get dismissible() {
    return props.interactive
  },
}

function place(): void {
  const anchor = props.anchor
  const panel = panelEl.value
  if (!anchor || !panel) return

  const style = panel.style
  // Measure UNCAPPED: with a `max-height` still applied the panel would report
  // the height we forced on it last time, conclude it fits, uncap itself and
  // oscillate between the two on every frame.
  style.maxHeight = ''
  // Width before height, because width is what decides how the content wraps.
  style.width = props.matchAnchorWidth ? `${anchor.getBoundingClientRect().width}px` : ''

  const a = anchor.getBoundingClientRect()
  const natural = panel.getBoundingClientRect()
  const viewWidth = window.innerWidth
  const viewHeight = window.innerHeight
  const margin = VIEWPORT_MARGIN
  const width = natural.width

  let height = natural.height
  let cap: number | undefined
  let top: number
  let left: number

  if (props.side === 'bottom' || props.side === 'top') {
    const below = viewHeight - margin - a.bottom - props.gap
    const above = a.top - props.gap - margin
    const useBelow =
      props.side === 'bottom' ? keepSide(height, below, above) : !keepSide(height, above, below)
    const room = useBelow ? below : above
    if (height > room && room >= MIN_PANEL) {
      cap = room
      height = room
    }
    top = useBelow ? a.bottom + props.gap : a.top - props.gap - height
    left = props.align === 'start' ? a.left : a.right - width
  } else {
    const roomRight = viewWidth - margin - a.right - props.gap
    const roomLeft = a.left - props.gap - margin
    const useRight =
      props.side === 'right' ? keepSide(width, roomRight, roomLeft) : !keepSide(width, roomLeft, roomRight)
    const available = viewHeight - 2 * margin
    if (height > available) {
      cap = available
      height = available
    }
    left = useRight ? a.right + props.gap : a.left - props.gap - width
    top = props.align === 'start' ? a.top : a.bottom - height
  }

  // The shift. Flipping answers "which side"; this answers "and still on
  // screen": the palette is a 220px column whose last rows sit at the window's
  // bottom edge, and the inspector is a 312px column against its right one.
  style.top = `${Math.round(clamp(top, margin, viewHeight - margin - height))}px`
  style.left = `${Math.round(clamp(left, margin, viewWidth - margin - width))}px`
  if (cap !== undefined) style.maxHeight = `${Math.round(cap)}px`
  style.zIndex = String(zIndex)
  placed.value = true

  const settled = panel.getBoundingClientRect()
  imposed = { width: settled.width, height: settled.height }
}

function onViewportChange(event: Event): void {
  // A scroll INSIDE the panel is the panel's own list being read. Following it
  // would be wrong and closing on it would be hostile.
  if (
    event.type === 'scroll' &&
    event.target instanceof Node &&
    panelEl.value?.contains(event.target) === true
  ) {
    return
  }
  if (props.follow) place()
  else emit('close')
}

function onPointerDown(event: PointerEvent): void {
  const target = event.target
  const start = layers.indexOf(layer)
  if (!(target instanceof Node) || start < 0) return
  // Anything belonging to this panel, to its anchor, or to a panel opened FROM
  // it is inside.
  for (let index = start; index < layers.length; index += 1) {
    const above = layers[index]
    if (above.panel()?.contains(target) === true || above.anchor()?.contains(target) === true) {
      return
    }
  }
  emit('close')
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return
  // Innermost first, and only that one: with a picker open inside a socket's
  // popover, one Escape should close the picker, not both.
  // `lib` is es2022 here, so no `findLast`.
  let innermost: Layer | undefined
  for (let index = layers.length - 1; index >= 0 && innermost === undefined; index -= 1) {
    const entry = layers[index]
    if (entry.dismissible) innermost = entry
  }
  if (innermost !== layer) return
  event.stopPropagation()
  emit('close')
}

function attach(): void {
  const panel = panelEl.value
  if (!panel || layers.includes(layer)) return
  zIndex = BASE_Z + layers.length * 10
  layers.push(layer)
  place()
  // A belt to `onUpdated`'s braces: content can change size without Vue
  // re-rendering anything — a web font landing, an image, a field somebody
  // drag-resizes. The guard is what stops the height cap from oscillating,
  // since applying it is itself a resize.
  observer = new ResizeObserver(() => {
    const rect = panel.getBoundingClientRect()
    if (Math.abs(rect.width - imposed.width) < 1 && Math.abs(rect.height - imposed.height) < 1) {
      return
    }
    place()
  })
  observer.observe(panel)
  // Capture, because the pane that scrolls is an ancestor of the anchor and
  // its scroll event does not reach `window` on its own.
  window.addEventListener('scroll', onViewportChange, true)
  window.addEventListener('resize', onViewportChange)
  if (props.interactive) {
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeydown, true)
  }
}

function detach(): void {
  const index = layers.indexOf(layer)
  if (index >= 0) layers.splice(index, 1)
  observer?.disconnect()
  observer = undefined
  imposed = { width: -1, height: -1 }
  placed.value = false
  window.removeEventListener('scroll', onViewportChange, true)
  window.removeEventListener('resize', onViewportChange)
  document.removeEventListener('pointerdown', onPointerDown, true)
  document.removeEventListener('keydown', onKeydown, true)
}

// `post`, so the panel exists to be measured when `open` turns true.
watch(() => props.open, (open) => (open ? attach() : detach()), {
  immediate: true,
  flush: 'post',
})

// The palette's tooltip stays open and changes anchor as the pointer crosses
// rows, which is the whole reason it does not flicker.
watch(
  () => props.anchor,
  () => {
    if (props.open) place()
  },
  { flush: 'post' },
)

/**
 * The slot's content decides the panel's size, so a re-render is a
 * re-measure: the variable picker goes from one "buscando" row to twenty-five
 * results, and a panel placed for the first would hang off the screen for the
 * second. This is the RELIABLE trigger — `ResizeObserver` is the fallback,
 * not the mechanism.
 *
 * `place()` writes styles and sets `placed` (already true by now), so it
 * cannot re-enter this hook.
 */
onUpdated(() => {
  if (props.open) place()
})

onBeforeUnmount(detach)

/** For an anchor that moves without a scroll event — Vue Flow's own panning. */
defineExpose({ reposition: place })
</script>

<template>
  <Teleport to="body">
    <!-- `invisible` until the first measurement: the panel has to be in the
         document to be measured, and without this it would be painted once at
         wherever the document flow put it. -->
    <div
      v-if="open"
      ref="panelEl"
      v-bind="$attrs"
      class="fixed"
      :class="[interactive ? '' : 'pointer-events-none', placed ? '' : 'invisible']"
    >
      <slot />
    </div>
  </Teleport>
</template>
